import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * Signatures — inline case-detail widget for the cm-fe Case page.
 *
 * Mirrors the Scale-portal Signatures widget (CaseManagement/CaseDetail/
 * Signatures/Signatures.jsx) at the layout level — parties table, captured
 * signatures count, preview agreement modal, download zip, per-row resend —
 * but reuses cm-fe's existing endpoints instead of introducing a new
 * per-case BE route:
 *
 *   1. GET /signing/requests?caseId=:id  → locate the request (one per case)
 *   2. GET /signing/requests/:id         → enriched payload with case_clients
 *                                          + alternate_emails + signatures
 *   3. POST /signing/requests/:id/resend → per-row resend (clientId or email)
 *   4. GET /signing/requests/:id/download → zip with agreement + signed PDFs
 *
 * This is intentionally a smaller surface than the full SignatureDetail page
 * — the CM can still drill into the dedicated page via the "Open detail
 * page" link in the header. The captured-signature breakdown lives there.
 */

function shortDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

// Captured-signature rendering — mirrors the scale-portal Signatures widget so
// the agreement preview shows the same signature section (typed font, drawn
// image, or data-URL) the portal does.
const TYPED_FONTS = {
    caveat: "'Caveat', 'Brush Script MT', cursive",
    dancing: "'Dancing Script', 'Brush Script MT', cursive",
    dancing_script: "'Dancing Script', 'Brush Script MT', cursive",
    satisfy: "'Satisfy', 'Lucida Handwriting', cursive",
    pacifico: "'Pacifico', 'Brush Script MT', cursive",
    allura: "'Allura', 'Lucida Handwriting', cursive",
}

function CapturedSignature({ signatureData }) {
    if (!signatureData) {
        return <span className="text-xs text-gray-400">—</span>
    }
    if (typeof signatureData === 'object' && signatureData.type === 'typed') {
        const font = TYPED_FONTS[signatureData.font] || TYPED_FONTS.caveat
        return (
            <span style={{ fontFamily: font, fontSize: 22, color: '#0f172a', lineHeight: 1 }}>
                {signatureData.value || ''}
            </span>
        )
    }
    if (typeof signatureData === 'object' && signatureData.type === 'drawn') {
        return <img src={signatureData.value} alt="signature" style={{ maxHeight: 48, maxWidth: 220, display: 'block' }} />
    }
    if (typeof signatureData === 'string' && signatureData.startsWith('data:')) {
        return <img src={signatureData} alt="signature" style={{ maxHeight: 48, maxWidth: 220, display: 'block' }} />
    }
    return <span className="text-xs text-gray-400">—</span>
}

const previewSigCell = {
    padding: '10px 12px',
    fontSize: 12,
    color: '#1f2937',
    borderRight: '1px solid #e5e7eb',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'middle',
}

export default function Signatures({ onToast }) {
    const ctx = useContext(CaseDetailContext)
    const caseId = ctx?.caseData?.id

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [request, setRequest] = useState(null)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [resendingKey, setResendingKey] = useState(null)
    const [copiedLink, setCopiedLink] = useState(false)

    // Two-step load: list-by-caseId returns the row (without enriched
    // case_clients alternates), then we fetch the detail endpoint to
    // pick up alternate_emails for the per-email party rows. List → 0
    // rows means "no signing request yet" — show the empty state. List
    // → 1 row → detail enrichment. We avoid the GET /requests/case/:id
    // endpoint here because it AUTO-CREATES a request as a side effect,
    // which we don't want on a passive widget render.
    const fetchData = useCallback(async () => {
        if (!caseId) return
        setLoading(true)
        setError(null)
        try {
            const listRes = await api.get(`/signing/requests?caseId=${caseId}`)
            const rows = listRes.data?.data || listRes.data || []
            const first = Array.isArray(rows) ? rows[0] : null
            if (!first?.id) {
                setRequest(null)
                return
            }
            const detailRes = await api.get(`/signing/requests/${first.id}`)
            setRequest(detailRes.data?.data || detailRes.data || null)
        } catch (e) {
            console.error('[case-detail Signatures] load failed', e)
            setError(e?.response?.data?.message || 'Could not load signatures.')
        } finally {
            setLoading(false)
        }
    }, [caseId])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const session = request?.signers?.[0] || {}
    const signatures = Array.isArray(session.signatures) ? session.signatures : []
    const caseClients = Array.isArray(session.case_clients) ? session.case_clients : []

    // One row per EMAIL — the case_client's primary email plus each
    // alternate_emails[] entry, mirroring the scale-portal Signatures widget so
    // alternate emails get their own signed/pending + Resend row. Name is the
    // typed-at-signing name when a matching signature exists, else the
    // case_client's name.
    const partyRows = useMemo(() => {
        const rows = caseClients.flatMap((c) => {
            const alternates = Array.isArray(c.alternate_emails)
                ? c.alternate_emails.filter(Boolean)
                : []
            const entries = [
                ...(c.email ? [{ email: c.email, isPrimary: true }] : []),
                ...alternates.map((alt) => ({ email: alt, isPrimary: false })),
            ]
            return entries.map((entry) => {
                const sig = signatures.find(
                    (s) =>
                        s?.email &&
                        String(s.email).toLowerCase() === String(entry.email).toLowerCase(),
                )
                const typedName = typeof sig?.name === 'string' ? sig.name.trim() : ''
                const typedRole = typeof sig?.role === 'string' ? sig.role.trim() : ''
                return {
                    _rowKey: `${c.client_id || c.email}-${entry.email}`,
                    client_id: c.client_id,
                    name: typedName || c.name || '',
                    email: entry.email,
                    isPrimary: entry.isPrimary,
                    role: typedRole || c.role,
                    status: sig ? 'signed' : 'pending',
                    signedAt: sig?.signedAt || null,
                    isOtherSigner: false,
                }
            })
        })

        // Append "other signers" — anyone who actually signed but is NOT a
        // case_client primary/alternate. Captures the full signature roster
        // on the parties table (e.g., someone who joined via a shared link
        // after the SR snapshot was taken). Other-signer rows render the
        // typed name / role from the captured signature, status Signed,
        // no Resend button, and an "Outside" pill in the name cell.
        const knownEmails = new Set(
            rows
                .map((r) => String(r.email || '').toLowerCase())
                .filter(Boolean),
        )
        const otherSigners = (signatures || [])
            .filter(
                (s) =>
                    s?.email &&
                    !knownEmails.has(String(s.email).toLowerCase()),
            )
            .map((s, idx) => {
                const typedName = typeof s?.name === 'string' ? s.name.trim() : ''
                const typedRole = typeof s?.role === 'string' ? s.role.trim() : ''
                return {
                    _rowKey: `other-${s.email}-${s.signedAt || idx}`,
                    client_id: null,
                    name: typedName || s.email,
                    email: s.email,
                    isPrimary: false,
                    role: typedRole,
                    status: 'signed',
                    signedAt: s.signedAt || null,
                    isOtherSigner: true,
                }
            })

        return [...rows, ...otherSigners]
    }, [caseClients, signatures])

    const previewHtml = useMemo(() => {
        const raw = request?.html_content || ''
        if (!raw) return ''
        return DOMPurify.sanitize(raw, {
            FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
        })
    }, [request])

    const handleResend = async (row) => {
        if (!row || !request?.id) return
        const key = row.email || `client-${row.client_id}`
        setResendingKey(key)
        try {
            const payload = row.isPrimary
                ? { clientId: row.client_id }
                : { email: row.email }
            const res = await api.post(`/signing/requests/${request.id}/resend`, payload)
            const status = res.data?.emailDispatchStatus
            const label = row.isPrimary ? (row.name || 'party') : (row.email || 'recipient')
            if (status === 'failed') {
                onToast?.({ type: 'error', message: `Failed to resend to ${label}.` })
            } else {
                onToast?.({ type: 'success', message: `Signing link resent to ${label}.` })
                // Re-fetch so any rotated token / signing_url is visible.
                fetchData()
            }
        } catch (e) {
            onToast?.({
                type: 'error',
                message: e?.response?.data?.message || 'Failed to resend signing link.',
            })
        } finally {
            setResendingKey(null)
        }
    }

    const handleDownload = async () => {
        if (downloading || !request?.id) return
        setDownloading(true)
        try {
            const res = await api.get(`/signing/requests/${request.id}/download`, {
                responseType: 'blob',
            })
            const blob = new Blob([res.data], { type: 'application/zip' })
            const cd = res.headers?.['content-disposition'] || ''
            const m = cd.match(/filename="?([^";]+)"?/i)
            const filename = (m && m[1]) || `signing-bundle-${request.id}.zip`
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            a.remove()
            setTimeout(() => URL.revokeObjectURL(url), 1000)
        } catch (e) {
            let message = 'Failed to download the signing bundle.'
            try {
                if (e?.response?.data && typeof e.response.data.text === 'function') {
                    const text = await e.response.data.text()
                    const parsed = JSON.parse(text)
                    if (parsed?.message) message = parsed.message
                }
            } catch (_e) { /* keep default */ }
            onToast?.({ type: 'error', message })
        } finally {
            setDownloading(false)
        }
    }

    // Loading skeleton matches the rest of the cm-fe case-detail card
    // chrome (white card, gray header).
    const Header = ({ children }) => (
        <div className="px-5 md:px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <div>
                <div className="text-base font-semibold text-gray-900">Signatures</div>
                <p className="text-xs text-gray-500">
                    {request
                        ? `${signatures.length} signature${signatures.length === 1 ? '' : 's'} captured`
                        : 'Agreement signing status for this case'}
                </p>
            </div>
            {children}
        </div>
    )

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <Header />
                <div className="p-10 text-center text-sm text-gray-500">Loading signatures…</div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <Header />
                <div className="p-10 text-center text-sm text-red-500">{error}</div>
            </div>
        )
    }

    if (!request) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <Header />
                <div className="p-12 flex flex-col items-center justify-center text-center">
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" className="text-gray-300 mb-3" aria-hidden="true">
                        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                    <div className="text-base font-semibold text-gray-700">No signing request</div>
                    <p className="text-xs text-gray-500 mt-1">This case doesn&apos;t have a signing request yet.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <Header>
                <div className="flex gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => {
                            const url = session?.signing_url || ''
                            if (!url) {
                                onToast?.({ type: 'warning', message: 'No signing URL on this request yet.' })
                                return
                            }
                            if (!navigator.clipboard?.writeText) {
                                onToast?.({ type: 'error', message: 'Clipboard not available in this browser.' })
                                return
                            }
                            navigator.clipboard
                                .writeText(url)
                                .then(() => {
                                    setCopiedLink(true)
                                    setTimeout(() => setCopiedLink(false), 2000)
                                    onToast?.({ type: 'success', message: 'Signing link copied to clipboard.' })
                                })
                                .catch(() => onToast?.({ type: 'error', message: 'Could not copy link to clipboard.' }))
                        }}
                        disabled={!session?.signing_url}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50"
                        title={session?.signing_url ? 'Copy the public signing link to clipboard' : 'No signing URL on this request yet'}
                    >
                        {copiedLink ? 'Copied!' : 'Copy Signing Link'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setPreviewOpen(true)}
                        disabled={!previewHtml}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50"
                        title={previewHtml ? 'Preview the agreement document' : 'Agreement HTML unavailable'}
                    >
                        Preview Agreement
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloading}
                        className="px-3 py-1.5 text-xs font-medium bg-[#173336] text-white hover:bg-[#0d2226] rounded-md disabled:opacity-50"
                    >
                        {downloading ? 'Preparing zip…' : 'Download (.zip)'}
                    </button>
                    <a
                        href={`/signatures/${request.id}`}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 rounded-md inline-flex items-center"
                    >
                        Open detail page
                    </a>
                </div>
            </Header>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[720px]">
                    <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/50">
                            <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-gray-500">Name</th>
                            <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-gray-500">Email</th>
                            <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-gray-500">Role</th>
                            <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-gray-500">Status</th>
                            <th className="text-left px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-gray-500">Signed At</th>
                            <th className="text-right px-5 py-3 text-[11px] uppercase tracking-wide font-semibold text-gray-500">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {partyRows.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-400">
                                    No parties on this signing request.
                                </td>
                            </tr>
                        ) : (
                            partyRows.map((p) => {
                                const rowKey = p.email || `client-${p.client_id}`
                                const inFlight = resendingKey === rowKey
                                const disabled =
                                    inFlight ||
                                    p.status === 'signed' ||
                                    request.status === 'completed' ||
                                    (p.isPrimary && !p.client_id)
                                const title = p.status === 'signed'
                                    ? 'This email has already signed'
                                    : request.status === 'completed'
                                        ? 'Signing request is closed'
                                        : 'Resend signing link to this email'
                                return (
                                    <tr key={p._rowKey} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="px-5 py-3 text-[13px] font-medium text-gray-900">
                                            <span>{p.name || '—'}</span>
                                        </td>
                                        <td className="px-5 py-3 text-[13px] text-gray-600">
                                            <span style={{ wordBreak: 'break-all' }}>{p.email || '—'}</span>
                                        </td>
                                        <td className="px-5 py-3 text-[13px] text-gray-600 capitalize">
                                            {p.role || (p.isOtherSigner ? 'Other Signer' : '—')}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                                                style={
                                                    p.status === 'signed'
                                                        ? { background: '#d1fae5', color: '#065f46' }
                                                        : { background: '#f1f5f9', color: '#475569' }
                                                }
                                            >
                                                {p.status === 'signed' ? 'Signed' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-[13px] text-gray-600">{shortDate(p.signedAt) || '—'}</td>
                                        <td className="px-5 py-3 text-right">
                                            {p.isOtherSigner ? (
                                                <span className="text-xs text-gray-400">—</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleResend(p)}
                                                    disabled={disabled}
                                                    title={title}
                                                    className="px-2.5 py-1 text-[11px] font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    {inFlight ? 'Sending…' : 'Resend'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {previewOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setPreviewOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: 16,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#fff',
                            borderRadius: 12,
                            maxWidth: 920,
                            width: '100%',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
                        }}
                    >
                        <div className="flex items-center justify-between" style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb' }}>
                            <div className="text-sm font-semibold text-gray-900">Agreement Preview</div>
                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                aria-label="Close preview"
                                className="w-7 h-7 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                            >
                                ✕
                            </button>
                        </div>
                        <div style={{ overflow: 'auto', padding: '32px 40px', flex: 1 }}>
                            {/* Mirror the scale-portal preview: document typography +
                                the captured-signatures table appended below the
                                agreement, so the CM preview shows the same canvas
                                (and the actual signatures) the portal does. */}
                            <style>{`
                              .agreement-preview-body { color: #1f2937; line-height: 1.625; }
                              .agreement-preview-body h1 { font-size: 24px; font-weight: 700; text-align: center; margin: 0 0 28px; }
                              .agreement-preview-body h2 { font-size: 16px; font-weight: 600; margin: 24px 0 10px; }
                              .agreement-preview-body h3 { font-size: 14px; font-weight: 600; margin: 18px 0 6px; }
                              .agreement-preview-body p  { font-size: 14px; line-height: 24px; margin: 10px 0; }
                              /* Lists are BARE by default; markers are opt-in per
                                 document. The BE stamps data-list-markers="on" when
                                 it renders a NEW signing request, and only when the
                                 item text doesn't already carry its own "4)"
                                 numbering. So agreements sent before markers shipped
                                 keep rendering exactly as they were sent, and Word
                                 imports that number themselves never double-number. */
                              .agreement-preview-body ul,
                              .agreement-preview-body ol { margin: 10px 0; padding-left: 0 !important; list-style: none !important; }
                              .agreement-preview-body ul[data-list-markers='on'],
                              .agreement-preview-body ol[data-list-markers='on'] { padding-left: 24px !important; }
                              .agreement-preview-body ul[data-list-markers='on'] { list-style: disc !important; }
                              .agreement-preview-body ol[data-list-markers='on'] { list-style: decimal !important; }
                              .agreement-preview-body ul[data-list-markers='on'] ul { list-style: circle !important; }
                              .agreement-preview-body ol[data-list-markers='on'] ol { list-style: lower-alpha !important; }
                              .agreement-preview-body li { font-size: 14px; line-height: 24px; margin: 4px 0; }
                              .agreement-preview-body li > p { margin: 0; }
                              .agreement-preview-body a { color: #0f766e; text-decoration: underline; }
                              .agreement-preview-body strong { font-weight: 600; }
                              .agreement-preview-body img { margin: 10px 0; border-radius: 2px; }
                              .agreement-preview-body table { width: 100%; margin: 14px 0; border-collapse: collapse; }
                              .agreement-preview-body th { border: 1px solid #d1d5db; background: #f9fafb; padding: 6px 10px; text-align: left; font-size: 12px; font-weight: 600; }
                              .agreement-preview-body td { border: 1px solid #d1d5db; padding: 6px 10px; font-size: 12px; vertical-align: top; }
                            `}</style>
                            <div
                                className="agreement-preview-body"
                                style={{ color: '#1f2937', fontSize: 14, lineHeight: 1.6 }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                                {/* Captured signatures appended inside the agreement
                                    document — same layout as the scale-portal preview
                                    and the public sign page. */}
                                {signatures.length > 0 && (
                                    <table
                                        style={{
                                            width: '100%',
                                            margin: '24px 0 8px',
                                            borderCollapse: 'collapse',
                                            border: '1px solid #e5e7eb',
                                            tableLayout: 'fixed',
                                        }}
                                    >
                                        <colgroup>
                                            <col style={{ width: '18%' }} />
                                            <col style={{ width: '24%' }} />
                                            <col style={{ width: '36%' }} />
                                            <col style={{ width: '22%' }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                {['Date', 'Print Name', 'Signature', 'Role'].map((label, i, arr) => (
                                                    <th
                                                        key={label}
                                                        style={{
                                                            background: '#f9fafb',
                                                            padding: '8px 10px',
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            color: '#475569',
                                                            textAlign: 'left',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.04em',
                                                            borderRight: i === arr.length - 1 ? 'none' : '1px solid #e5e7eb',
                                                            borderBottom: '1px solid #e5e7eb',
                                                        }}
                                                    >
                                                        {label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {signatures.map((sig, idx) => {
                                                const dateLabel =
                                                    sig.date ||
                                                    (sig.signedAt
                                                        ? new Date(sig.signedAt).toLocaleDateString('en-US', {
                                                              year: 'numeric',
                                                              month: 'long',
                                                              day: 'numeric',
                                                          })
                                                        : '')
                                                return (
                                                    <tr
                                                        key={`sig-${idx}-${sig.signedAt || idx}`}
                                                        style={{ background: idx % 2 === 0 ? '#ffffff' : '#fafbfc' }}
                                                    >
                                                        <td style={previewSigCell}>{dateLabel}</td>
                                                        <td style={{ ...previewSigCell, fontWeight: 500 }}>{sig.name || ''}</td>
                                                        <td style={previewSigCell}>
                                                            <CapturedSignature signatureData={sig.signatureData} />
                                                        </td>
                                                        <td style={{ ...previewSigCell, borderRight: 'none', color: '#475569' }}>
                                                            {sig.role || ''}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
